import { Head, Link, useForm } from '@inertiajs/react'
import { FormEvent } from 'react'
import MarkdownEditor from '@/widgets/editor/MarkdownEditor'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { type PageNode } from '@/widgets/sidebar/PageTree'

interface Workspace {
  id: number
  name: string
  slug: string
}

interface NewProps {
  workspace: Workspace
  parent_id: number | null
  sidebar_pages: PageNode[]
  user_role: 'admin' | 'editor' | 'viewer'
}

function New({ workspace, parent_id, user_role }: NewProps) {
  const { data, setData, post, processing, errors } = useForm({
    title: '',
    content_md: '',
    parent_id: parent_id || '',
    is_public: false,
  })

  const isViewer = user_role === 'viewer'

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(`/${workspace.slug}/-/create`)
  }

  return (
    <>
      <Head title={`New Page - ${workspace.name}`} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {isViewer && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                Your page will be submitted for admin review.
              </div>
            )}

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                id="title"
                type="text"
                value={data.title}
                onChange={(e) => setData('title', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Page title"
                autoFocus
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Content
              </label>
              <MarkdownEditor
                content={data.content_md}
                onChange={(markdown) => setData('content_md', markdown)}
                placeholder="Write your content in markdown..."
              />
              {errors.content_md && (
                <p className="mt-1 text-sm text-red-600">{errors.content_md}</p>
              )}
            </div>

            {!isViewer && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={data.is_public}
                  onChange={(e) => setData('is_public', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="is_public" className="text-sm text-gray-700">
                  Make this page public
                </label>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Link
                href={`/${workspace.slug}`}
                className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={processing}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                  isViewer
                    ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                }`}
              >
                {isViewer
                  ? (processing ? 'Submitting...' : 'Submit proposal')
                  : (processing ? 'Creating...' : 'Create Page')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

New.layout = withWorkspaceLayout
export default New
